/**
 * The drawer's exception-details section - rendered INSIDE the primary
 * status card (above Review actions, divided by a thin rule). The Why
 * section is fed by the backend-built `row.detail_blocks` (see the backend
 * `apply_detail_blocks` seam); the How section reuses it for resource links.
 * It carries the per-row context that used to live in the legacy in-table
 * expand (affected line items, surfacing invoices, supporting notes).
 *
 * The heading defaults to "Why this was flagged"; the How section passes its
 * own heading + `showTopDivider={false}` so the two share one rule.
 *
 * Content is a flat, ordered list of typed blocks - `heading`, `text`, `list`
 * - sharing one inline shape (text + optional link + icon). `heading` blocks
 * provide the structure that titled groups used to:
 *  - a plain string                  -> one text block
 *  - an ExceptionDrawerDetailBlock[] -> rendered in array order
 *
 * Built for the drawer's narrow column (`clamp(460px, 32vw, 520px)`). Long
 * list blocks cap at 8 with a tooltip listing the full set, so a ~100-invoice
 * row keeps the card compact.
 */
import React from 'react'
import { Box, Divider, Link, Tooltip, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import type {
  ExceptionDrawerDetailBlock,
  ExceptionDrawerInline,
} from '../../../../config/exceptions/types'
import {
  LINK_STYLES,
  type ReferenceLinkType,
} from '../../../shared/referenceLinks/linkStyles'
import { poolwerxColors } from '../../../../theme/colors'
import SectionHeading from './SectionHeading'

type RawDetails =
  | string
  | ExceptionDrawerDetailBlock[]
  | null
  | undefined

interface ExceptionDetailsSectionProps {
  details: RawDetails
  /** Section heading; defaults to "Why this was flagged". */
  heading?: string
  /**
   * Render the leading rule. Defaults to true. The second section in a
   * stack (e.g. "How to fix" under "Why this was flagged") sets this false
   * so the two sections share one rule instead of doubling it.
   */
  showTopDivider?: boolean
  /**
   * Drop the heading's top margin so it sits flush at the top of its container.
   * Set by the expanded-row body's first ("Why this was flagged") section so it
   * opens right at the top; the stacked "How" section keeps its margin for
   * separation.
   */
  tightTop?: boolean
}

const DEFAULT_HEADING = 'Why this was flagged'

// Items beyond this cap collapse into a "+N more" line whose tooltip lists
// the full set - keeps the in-card list short without losing the data.
const ITEM_CAP = 8

const normalise = (raw: RawDetails): ExceptionDrawerDetailBlock[] => {
  if (raw == null) return []
  if (typeof raw === 'string') {
    const text = raw.trim()
    return text ? [{ type: 'text', text }] : []
  }
  return Array.isArray(raw) ? raw : []
}

const toInline = (item: string | ExceptionDrawerInline): ExceptionDrawerInline =>
  typeof item === 'string' ? { text: item } : item

// Allow only site-relative or http(s) URLs - blocks javascript:/data:/etc.
// detail_blocks links (e.g. skimmer kb_url) aren't run through the
// reference-link resolver's https-only filter, so guard here before
// rendering links or icon images from any data-driven source.
const isSafeUrl = (url: string): boolean => {
  const u = url.trim()
  return u.startsWith('/') || /^https?:\/\//i.test(u)
}

// Branded leading glyph for a known reference-link type. Constrained to the
// five LINK_STYLES keys (no arbitrary MUI icon names), so this is a vetted
// component - no URL guard needed.
const renderGlyph = (iconType?: ReferenceLinkType): React.ReactNode => {
  if (!iconType) return null
  const { icon: Glyph, fg } = LINK_STYLES[iconType]
  return <Glyph sx={{ fontSize: 16, color: fg, flexShrink: 0 }} />
}

// The single place link + icon behaviour lives. Reused by heading, text and
// every list item, so adding an affordance here applies everywhere.
const InlineContent: React.FC<{ inline: ExceptionDrawerInline }> = ({
  inline,
}) => {
  const imageIcon =
    inline.icon && isSafeUrl(inline.icon) ? (
      <Box
        component="img"
        src={inline.icon}
        alt=""
        aria-hidden
        sx={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }}
      />
    ) : null

  // A type glyph wins the leading slot over an image icon.
  const glyph = renderGlyph(inline.iconType)
  const leading = glyph ?? imageIcon

  if (inline.url && isSafeUrl(inline.url)) {
    const target = inline.target ?? '_blank'
    const external = target === '_blank'
    return (
      <Link
        href={inline.url}
        target={target}
        rel={external ? 'noopener noreferrer' : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontWeight: 500,
          wordBreak: 'break-word',
        }}
      >
        {leading}
        {inline.text}
        {/* A branded glyph already signals the link type, so skip the
         * redundant trailing arrow when one is present. */}
        {external && !glyph && <OpenInNewIcon fontSize="inherit" />}
      </Link>
    )
  }

  if (!leading) return <>{inline.text}</>
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {leading}
      {inline.text}
    </Box>
  )
}

const ListBlock: React.FC<{
  items: Array<string | ExceptionDrawerInline>
}> = ({ items }) => {
  if (items.length === 0) return null
  const shown = items.slice(0, ITEM_CAP)
  const overflow = items.length - shown.length
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.25 } }}>
      {shown.map((item, idx) => (
        <li key={idx}>
          <Typography
            component="span"
            variant="body2"
            sx={{ wordBreak: 'break-word' }}
          >
            <InlineContent inline={toInline(item)} />
          </Typography>
        </li>
      ))}
      {overflow > 0 && (
        <Tooltip
          arrow
          placement="top"
          title={
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {items.map((item, idx) => (
                <li key={idx}>{toInline(item).text}</li>
              ))}
            </Box>
          }
        >
          <Box
            component="li"
            sx={{
              listStyle: 'none',
              ml: -2.5,
              color: 'text.secondary',
              fontSize: '0.8rem',
              cursor: 'default',
              width: 'fit-content',
            }}
          >
            +{overflow} more
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

const DetailBlockView: React.FC<{ block: ExceptionDrawerDetailBlock }> = ({
  block,
}) => {
  switch (block.type) {
    case 'heading':
      return (
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          <InlineContent inline={block} />
        </Typography>
      )
    case 'text':
      return (
        <Typography
          variant="body2"
          sx={{
            fontSize: '13.5px',
            lineHeight: 1.55,
            color: poolwerxColors.text.secondary,
          }}
        >
          <InlineContent inline={block} />
        </Typography>
      )
    case 'list':
      return <ListBlock items={block.items} />
    default:
      return null
  }
}

const ExceptionDetailsSection: React.FC<ExceptionDetailsSectionProps> = ({
  details,
  heading = DEFAULT_HEADING,
  showTopDivider = true,
  tightTop = false,
}) => {
  const blocks = normalise(details)
  if (blocks.length === 0) return null
  return (
    <>
      {showTopDivider && <Divider sx={{ my: 1.25 }} />}
      <SectionHeading tightTop={tightTop}>{heading}</SectionHeading>
      {blocks.map((block, idx) => (
        <Box
          key={idx}
          sx={
            idx === 0
              ? undefined
              : // A heading opens a new section (more breathing room);
                // text/list flow tighter under what precedes them.
                { mt: block.type === 'heading' ? 1.25 : 0.75 }
          }
        >
          <DetailBlockView block={block} />
        </Box>
      ))}
    </>
  )
}

export default ExceptionDetailsSection
